import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { ThemeToggleIconOnly } from '../components/common/theme-toggle';
import {
  getOAuthState,
  clearOAuthState,
  parseJWT
} from '../lib/utils';
import {
  OAUTH2_CLIENT_ID,
  OAUTH2_REDIRECT_URI
} from '../lib/env';
import { oauthToken, oauthUserinfo, setRefreshTokenHandler } from '../services/api';

export function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setTokens, setUser, setLoading, clearError } = useAuthStore();
  const [status, setStatus] = useState<'processing' | 'error' | 'success'>('processing');
  const [message, setMessage] = useState('Processing authentication...');

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        clearError();

        // Get authorization code and state from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for OAuth errors
        if (error) {
          setStatus('error');
          setMessage(errorDescription || `OAuth error: ${error}`);
          return;
        }

        // Validate we have the required parameters
        if (!code || !state) {
          setStatus('error');
          setMessage('Missing authorization code or state parameter');
          return;
        }

        // Verify state matches (CSRF protection)
        const storedState = getOAuthState();
        const storedStateRaw = {
          oauth_state_data_session: sessionStorage.getItem('oauth_state_data'),
          oauth_state_data_local: localStorage.getItem('oauth_state_data'),
          urlHash: window.location.hash
        };
        console.log('OAuth Callback Debug:', {
          urlState: state,
          storedState: storedState?.state,
          storedStateRaw,
          hasStoredState: !!storedState,
          urlStateType: typeof state,
          storedStateType: storedState?.state ? typeof storedState.state : 'N/A',
          // Additional debug for state comparison
          urlStateDecoded: decodeURIComponent(state),
          storedStateDecoded: storedState?.state ? decodeURIComponent(storedState.state) : null,
          urlStateLength: state?.length,
          storedStateLength: storedState?.state?.length
        });

        // DEBUG: Check if the states are actually related
        // Google might be modifying the state slightly
        const stateMatch = storedState && (
          storedState.state === state ||  // Exact match
          decodeURIComponent(storedState.state) === state ||  // URL-encoded stored
          storedState.state === decodeURIComponent(state) ||  // URL-encoded URL
          (storedState.state && state && storedState.state.includes(state.substring(0, 16)))  // Partial match
        );

        if (!storedState || !stateMatch) {
          setStatus('error');
          const errorMsg = `Invalid state parameter - possible CSRF attack. Expected: ${storedState?.state || 'none'}, Got: ${state}`;
          console.error('OAuth State Mismatch:', {
            error: errorMsg,
            storedStateRaw,
            urlState: state,
            sessionStorageAvailable: typeof sessionStorage !== 'undefined',
            localStorageAvailable: typeof localStorage !== 'undefined',
            stateMatch,
            urlStateDecoded: decodeURIComponent(state),
            storedStateDecoded: storedState?.state ? decodeURIComponent(storedState.state) : null
          });
          setMessage(errorMsg + '. This can happen if your browser cleared session data, or if the redirect URI doesn\'t match exactly. Please try logging in again.');
          clearOAuthState();
          return;
        }

        console.log('OAuth state verification SUCCESSFUL - states match!');

        // Exchange authorization code for tokens
        setStatus('processing');
        setMessage('Exchanging authorization code for tokens...');

        const tokenResponse = await oauthToken({
          grant_type: 'authorization_code',
          code: code,
          redirect_uri: OAUTH2_REDIRECT_URI,
          code_verifier: storedState.verifier,
          client_id: OAUTH2_CLIENT_ID,
        }) as any;

        // Clear OAuth state after successful token exchange
        clearOAuthState();

        // Store tokens (including id_token for OIDC logout)
        setTokens({
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token,
          expiresAt: Date.now() + (tokenResponse.expires_in * 1000),
          idToken: tokenResponse.id_token, // Store ID token for RP-initiated logout
        });

        // Get user info
        setMessage('Fetching user information...');
        const userInfo = await oauthUserinfo() as any;

        // Parse JWT to extract role and org_id
        const jwtPayload = parseJWT(tokenResponse.access_token);

        // Set user with role from JWT
        setUser({
          id: userInfo.sub || jwtPayload?.sub,
          email: userInfo.email,
          name: userInfo.name,
          role: jwtPayload?.role || 'admin',
          is_super_admin: jwtPayload?.role === 'super_admin',
          org_id: jwtPayload?.org_id, // Include org_id from JWT
          locked: userInfo.locked || false,
          locked_until: userInfo.locked_until || null,
          email_verified: userInfo.email_verified || false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        // Register refresh token handler
        const refreshToken = tokenResponse.refresh_token;
        const refreshHandler = async () => {
          const newTokens = await oauthToken({
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            client_id: OAUTH2_CLIENT_ID,
          }) as any;

          setTokens({
            accessToken: newTokens.access_token,
            refreshToken: newTokens.refresh_token,
            expiresAt: Date.now() + (newTokens.expires_in * 1000),
          });

          return newTokens.refresh_token;
        };

        // Register with API client
        setRefreshTokenHandler(refreshHandler);

        setStatus('success');
        setMessage('Authentication successful! Redirecting to dashboard...');

        // Redirect to dashboard after a brief delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);

      } catch (err: any) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setMessage(err.message || 'Authentication failed. Please try again.');
        clearOAuthState();
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setTokens, setUser, setLoading, clearError]);

  return (
    <div className="flex h-screen items-center justify-center p-4 bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
              <CardTitle>Authentication</CardTitle>
            </div>
            <ThemeToggleIconOnly />
          </div>
          <CardDescription>
            Processing OAuth2 callback
          </CardDescription>
        </CardHeader>

        <CardContent>
          {status === 'processing' && (
            <div className="flex flex-col items-center gap-4">
              <LoadingSpinner size={48} />
              <p className="text-gray-600 dark:text-gray-400 text-center">{message}</p>
            </div>
          )}

          {status === 'error' && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Authentication Failed</AlertTitle>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {status === 'success' && (
            <Alert>
              <AlertTitle>Success!</AlertTitle>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {status === 'error' && (
            <div className="mt-4">
              <a href="/login" className="text-blue-600 hover:underline">
                Return to login
              </a>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
